// Replaces phone numbers and email addresses with ######## so contact details never show on the site.
// The API masks guest text the same way (GuestContentService.ScrubContactInfo); keep the two in step.
//
// Matching runs on a shadow of the text, each shadow character mapped back to the span of the original
// it came from, so masks land on the original and nothing else in it changes. Per character: format
// characters (zero-width spaces, joiners, soft hyphens, bidi controls) are dropped; the rest is
// NFKC-normalized (fullwidth digits and punctuation, circled and superscript digits read as ASCII), and
// then any remaining decimal digit reads as 0, any dash or minus sign as DASH_MARK, any space as a
// space, and NEL or a line or paragraph separator as a line break. DASH_MARK separates phone groups but
// is never part of an email, so an email next to a word joined by an en dash does not swallow the word.
// The API, whose .NET regex sees UTF-16 units, builds the same shadow and so matches the same text.
//
// The masked spans are the UNION of three matchers and an email scan, so this can only add masks to
// the 2026-10-17 rule (#6), with two narrow exceptions noted below:
//   - LIVE_RE is that rule: a 3-3-4 number with an optional 1 or +1 and up to three separators (dash,
//     dot, space, tab, line break) between groups. It runs from every position a match can start, not
//     only from the end of the previous match, so neither a dropped ladder nor a match that started at
//     an earlier number leaves the digits after it readable ("Rates 150 - 250 - 1602-555-0143"). The
//     exceptions:
//       - A ladder is dropped: a match whose two group separators are both exactly a space, a dash
//         and a space, and whose first and last groups are whole numbers, with no digit right before
//         or after them. Number ladders ("Pay 250 - 500 - 1000") have that shape, so "602 - 555 - 0143"
//         is left as typed too, an accepted trade-off. Any other separator, a line break included, is
//         masked, and so is a ladder whose last group runs on into more digits ("250 - 500 - 602555").
//         Text whose whitespace was collapsed passes { maskLadders: true }, because the collapse can
//         turn any wider separator into a ladder.
//       - A 1 prefix right after a letter or digit belongs to the text before it ("Rate $301 - 602...",
//         "Room B1 602..."), so it is trimmed off the match.
//   - GUARDED_RE (at most one separator between groups) and LONG_RE (a run of 10 to 15 digits), both
//     digit-guarded, mask what the trim leaves of a number glued to a word: the + of
//     "x+1 602-555-0143" and the 1 of "Call19175550186". LONG_RE also masks the + of an
//     international number.
// Emails are found by a linear scan with exactly the old pattern's reach, so long text cannot make
// it backtrack. Bidi override controls (LRO, RLO) are removed from the result: an override can make a
// reversed digit string display as a phone number.
//
// Memory follows the text and the number of masks, never the number of matches: each matcher merges
// overlapping spans as it finds them, the shadow is built with one join, and a character that NFKC
// expands is one entry in the shadow's map however many units it expands to.

const DASH_MARK = '\uE000';
const LIVE_RE = /(\+?1[-. \t\n\r\v\f\uE000]{0,3})?\(?[0-9]{3}\)?([-. \t\n\r\v\f\uE000]{0,3})[0-9]{3}([-. \t\n\r\v\f\uE000]{0,3})[0-9]{4}/g;
const GUARDED_RE = /(?:\+1[-. \t\uE000]?)?\(?[0-9]{3}\)?[-. \t\uE000]?[0-9]{3}[-. \t\uE000]?[0-9]{4}(?![0-9])/g;
const LONG_RE = /\+?[0-9]{10,15}(?![0-9])/g;
const BIDI_OVERRIDE = /[\u202D\u202E]/g;
const FORMAT_CHAR = /\p{Cf}/u;
const DIGIT_CHAR = /\p{Nd}/u;
const DASH_CHAR = /\p{Pd}/u;
const SPACE_CHAR = /\p{Zs}/u;
const NON_ASCII = /[^\x00-\x7F]/;

export const CONTACT_MASK = '########';

// Spans are kept flat, [start, end, start, end, ...], sorted and disjoint.
type Spans = number[];

// The text the patterns run on, and where it came from. ASCII stands for itself. Each other character
// of the original is one entry: its shadow units run from `from` to `to` (none for a format
// character), and it starts at `at` in the original. The text between entries is ASCII, so a shadow
// index maps back by its offset from the entry before it.
type Shadow = { text: string; count: number; from: Int32Array; to: Int32Array; at: Int32Array };

// What one character of NFKC output reads as in the shadow.
function shadowUnit(c: string): string {
  if (c.charCodeAt(0) < 0x80) return c;
  if (DIGIT_CHAR.test(c)) return '0';
  if (DASH_CHAR.test(c) || c === '\u2212') return DASH_MARK;
  if (SPACE_CHAR.test(c)) return ' ';
  if (c === '\u0085' || c === '\u2028' || c === '\u2029') return '\n';
  return c;
}

// The UTF-16 units of the character at i: 2 for a surrogate pair, else 1.
const unitsAt = (text: string, i: number) =>
  (text.charCodeAt(i) & 0xfc00) === 0xd800 && (text.charCodeAt(i + 1) & 0xfc00) === 0xdc00 ? 2 : 1;

// Pure ASCII text is its own shadow.
function shadowOf(text: string): Shadow | null {
  if (!NON_ASCII.test(text)) return null;
  let wide = 0;
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) > 0x7f) wide++;
  const from = new Int32Array(wide);
  const to = new Int32Array(wide);
  const at = new Int32Array(wide);
  const parts: string[] = [];
  const pieces = new Map<string, string>();
  let count = 0;
  let size = 0;
  let run = 0;
  for (let i = 0; i < text.length; ) {
    if (text.charCodeAt(i) < 0x80) {
      i++;
      continue;
    }
    if (run < i) {
      parts.push(text.slice(run, i));
      size += i - run;
    }
    const ch = text.slice(i, i + unitsAt(text, i));
    let piece = pieces.get(ch);
    if (piece === undefined) {
      piece = '';
      if (!FORMAT_CHAR.test(ch)) for (const c of ch.normalize('NFKC')) piece += shadowUnit(c);
      pieces.set(ch, piece);
    }
    from[count] = size;
    at[count] = i;
    size += piece.length;
    to[count++] = size;
    if (piece) parts.push(piece);
    i += ch.length;
    run = i;
  }
  if (run < text.length) parts.push(text.slice(run));
  return { text: parts.join(''), count, from, to, at };
}

// Where shadow unit k came from: the start of its character in the original, or with end set, the end.
function originAt(text: string, sh: Shadow, k: number, end: boolean): number {
  let lo = 0;
  let hi = sh.count;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (sh.from[mid] <= k) lo = mid + 1;
    else hi = mid;
  }
  const after = end ? 1 : 0;
  if (lo === 0) return k + after;
  const a = sh.at[lo - 1];
  if (k < sh.to[lo - 1]) return end ? a + unitsAt(text, a) : a;
  return a + unitsAt(text, a) + (k - sh.to[lo - 1]) + after;
}

const isAsciiDigit = (c: number) => c >= 0x30 && c <= 0x39;
const isAlnum = (c: number) => isAsciiDigit(c) || (c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a);

// A group separator of the accepted ladder family: exactly a space, a dash and a space.
const isLadderSeparator = (sep: string | undefined) =>
  sep !== undefined && sep.length === 3 && sep[0] === ' ' && (sep[1] === '-' || sep[1] === DASH_MARK) && sep[2] === ' ';

// A ladder: both group separators are ladder separators, and no digit sits right before the first
// group or right after the last (charCodeAt past either end of the text is NaN, no digit).
function isLadder(t: string, m: RegExpExecArray): boolean {
  if (!(isLadderSeparator(m[2]) && isLadderSeparator(m[3]))) return false;
  let g = m.index + (m[1] ? m[1].length : 0);
  if (t.charCodeAt(g) === 0x28) g++;
  return !isAsciiDigit(t.charCodeAt(g - 1)) && !isAsciiDigit(t.charCodeAt(m.index + m[0].length));
}

// Adds [s, e) to spans, joined with every span it overlaps; spans that only touch stay apart. Spans
// arrive in start order, except that a trimmed prefix can be followed by a span that starts a few
// units before it and overlaps it.
function addSpan(spans: Spans, s: number, e: number): void {
  const n = spans.length;
  if (n === 0 || spans[n - 1] <= s) {
    spans.push(s, e);
    return;
  }
  if (spans[n - 2] <= s) {
    if (e > spans[n - 1]) spans[n - 1] = e;
    return;
  }
  let i = n;
  while (i > 0 && spans[i - 1] > s) i -= 2;
  let j = i;
  while (j < n && spans[j] < e) {
    if (spans[j] < s) s = spans[j];
    if (spans[j + 1] > e) e = spans[j + 1];
    j += 2;
  }
  spans.splice(i, j - i, s, e);
}

// LIVE_RE spans from every position a match can start, minus ladders, with a glued 1 prefix trimmed
// off. Each search resumes one unit past the last match's start, not at its end.
function liveSpans(t: string, maskLadders: boolean): Spans {
  const out: Spans = [];
  LIVE_RE.lastIndex = 0;
  for (let m = LIVE_RE.exec(t); m; m = LIVE_RE.exec(t)) {
    let s = m.index;
    const e = s + m[0].length;
    LIVE_RE.lastIndex = s + 1;
    if (maskLadders || !isLadder(t, m)) {
      if (m[1] && s > 0 && isAlnum(t.charCodeAt(s - 1))) s += m[1].length;
      addSpan(out, s, e);
    }
    // Inside a long run of digits, every start from here to 11 before the run's end matches 10 of its
    // digits (11 with a leading 1, trimmed after a digit), so one span stands for all those matches.
    const p0 = m.index + 1;
    let b = p0;
    while (b < t.length && isAsciiDigit(t.charCodeAt(b))) b++;
    const p1 = b - 11;
    if (p1 >= p0) {
      const trim = t.charCodeAt(p0) === 0x31 && isAlnum(t.charCodeAt(p0 - 1)) ? 1 : 0;
      addSpan(out, p0 + trim, p1 + 10 + (t.charCodeAt(p1) === 0x31 ? 1 : 0));
      LIVE_RE.lastIndex = p1 + 1;
    }
  }
  return out;
}

// Spans of every match of re that does not start right after a digit, the way a (?<![0-9])
// lookbehind would find them. Done in code because Safari before 16.4 cannot parse a regex
// lookbehind, and one it rejects takes down the whole client chunk.
function leadingGuardedSpans(re: RegExp, t: string): Spans {
  const out: Spans = [];
  re.lastIndex = 0;
  // Every pass moves past at least one UTF-16 unit, so t.length + 1 passes always suffice.
  for (let pass = 0, m = re.exec(t); m && pass <= t.length; pass++, m = re.exec(t)) {
    const s = m.index;
    if (m[0].length === 0 || (s > 0 && isAsciiDigit(t.charCodeAt(s - 1)))) {
      re.lastIndex = s + 1;
      continue;
    }
    addSpan(out, s, s + m[0].length);
  }
  return out;
}

const isLocal = (c: number) => isAlnum(c) || c === 0x2e || c === 0x5f || c === 0x25 || c === 0x2b || c === 0x2d;
const isDomain = (c: number) => isAlnum(c) || c === 0x2e || c === 0x2d;
const isLetter = (c: number) => (c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a);

// The spans [a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+[.][a-zA-Z]{2,} would match left to right, in linear time:
// for each at sign, the local run before it (not reaching back into the last match) and the rightmost
// dot in the domain run with at least one character before it and two letters after it.
function emailSpans(t: string): Spans {
  const out: Spans = [];
  let lastEnd = 0;
  for (let at = t.indexOf('@'); at !== -1; at = t.indexOf('@', at + 1)) {
    if (at < lastEnd) continue;
    let s = at;
    while (s > lastEnd && isLocal(t.charCodeAt(s - 1))) s--;
    if (s === at) continue;
    let r = at + 1;
    while (r < t.length && isDomain(t.charCodeAt(r))) r++;
    let end = -1;
    for (let d = r - 1; d > at + 1; d--) {
      if (t.charCodeAt(d) !== 0x2e) continue;
      let k = d + 1;
      while (k < r && isLetter(t.charCodeAt(k))) k++;
      if (k - (d + 1) >= 2) {
        end = k;
        break;
      }
    }
    if (end < 0) continue;
    addSpan(out, s, end);
    lastEnd = end;
  }
  return out;
}

// The spans scrubContactInfo masks, in UTF-16 units of the original, sorted and merged.
function maskSpans(text: string, maskLadders: boolean): Spans {
  const shadow = shadowOf(text);
  const t = shadow ? shadow.text : text;
  const lists = [liveSpans(t, maskLadders), leadingGuardedSpans(GUARDED_RE, t), leadingGuardedSpans(LONG_RE, t), emailSpans(t)];
  if (shadow) {
    for (const l of lists) {
      for (let k = 0; k < l.length; k += 2) {
        const e = l[k + 1];
        l[k] = originAt(text, shadow, l[k], false);
        l[k + 1] = originAt(text, shadow, e - 1, true);
      }
    }
  }
  // Each list is in start order, so the union takes the next span from whichever list starts first.
  const out: Spans = [];
  const pos = lists.map(() => 0);
  for (;;) {
    let best = -1;
    for (let k = 0; k < lists.length; k++) {
      if (pos[k] < lists[k].length && (best < 0 || lists[k][pos[k]] < lists[best][pos[best]])) best = k;
    }
    if (best < 0) return out;
    addSpan(out, lists[best][pos[best]], lists[best][pos[best] + 1]);
    pos[best] += 2;
  }
}

/** The spans of text that scrubContactInfo masks, in UTF-16 units of the original, merged and sorted. */
export function contactSpans(text: string, maskLadders = false): Array<[number, number]> {
  if (!text) return [];
  const flat = maskSpans(text, maskLadders);
  const out: Array<[number, number]> = [];
  for (let k = 0; k < flat.length; k += 2) out.push([flat[k], flat[k + 1]]);
  return out;
}

export function scrubContactInfo(text: string, opts?: { maskLadders?: boolean }): string {
  if (!text) return text;
  const spans = maskSpans(text, opts?.maskLadders === true);
  const parts: string[] = [];
  let pos = 0;
  for (let k = 0; k < spans.length; k += 2) {
    parts.push(text.slice(pos, spans[k]), CONTACT_MASK);
    pos = spans[k + 1];
  }
  parts.push(text.slice(pos));
  return parts.join('').replace(BIDI_OVERRIDE, '');
}

const POST_TEXT_FIELDS = ['title', 'Title', 'details', 'Details', 'description', 'Description', 'body', 'Body', 'stageName', 'StageName'];

/**
 * A post object with its text fields scrubbed, for any copy the server hands to a client component:
 * those props are serialized into the page HTML whether or not the component renders them.
 * Other fields (ids, slugs, media URLs, whose file names carry long digit runs) are left alone.
 */
export function scrubPostText<T>(post: T): T {
  if (!post || typeof post !== 'object') return post;
  const out: any = { ...(post as any) };
  for (const f of POST_TEXT_FIELDS) {
    if (typeof out[f] === 'string') out[f] = scrubContactInfo(out[f]);
  }
  if (out.owner && typeof out.owner === 'object' && typeof out.owner.displayName === 'string') {
    out.owner = { ...out.owner, displayName: scrubContactInfo(out.owner.displayName) };
  }
  return out;
}
